import type * as React from 'react'

import { cn } from '#lib/utils'

/**
 * Table primitives mirroring the shadcn `<Table>` API but rendered as
 * `<div>` elements with explicit ARIA roles. Designed for interactive,
 * virtualized data grids where native `<table>` layout has to be
 * overridden anyway — AG Grid and MUI X DataGrid use the same pattern.
 *
 * Uses `role='table'` (read-only) rather than `role='grid'`: the WAI-ARIA
 * Grid pattern requires roving tabindex + arrow-key navigation, none of
 * which is implemented here. `role='table'` is the honest downgrade and
 * matches what shadcn's `<Table>` does.
 *
 * @see https://www.w3.org/TR/wai-aria-1.2/#table
 */
function Grid({ className, ...props }: React.ComponentProps<'div'>) {
	return (
		<div
			className={cn('w-full text-sm', className)}
			data-slot='grid'
			role='table'
			{...props}
		/>
	)
}

function GridHeader({ className, ...props }: React.ComponentProps<'div'>) {
	return (
		<div
			className={cn('[&_[role=row]]:border-b', className)}
			data-slot='grid-header'
			role='rowgroup'
			{...props}
		/>
	)
}

function GridBody({ className, ...props }: React.ComponentProps<'div'>) {
	return (
		<div
			className={className}
			data-slot='grid-body'
			role='rowgroup'
			{...props}
		/>
	)
}

function GridRow({ className, ...props }: React.ComponentProps<'div'>) {
	return (
		<div
			className={cn(
				'flex border-b transition-colors hover:bg-muted has-aria-expanded:bg-muted data-[state=selected]:bg-muted',
				className
			)}
			data-slot='grid-row'
			role='row'
			{...props}
		/>
	)
}

function GridHead({ className, ...props }: React.ComponentProps<'div'>) {
	return (
		<div
			className={cn(
				'flex h-10 shrink-0 items-center px-2 text-start align-middle font-medium text-foreground',
				className
			)}
			data-slot='grid-head'
			role='columnheader'
			{...props}
		/>
	)
}

function GridCell({ className, ...props }: React.ComponentProps<'div'>) {
	return (
		<div
			className={cn(
				'flex shrink-0 items-center p-2 align-middle',
				className
			)}
			data-slot='grid-cell'
			role='cell'
			{...props}
		/>
	)
}

export { Grid, GridHeader, GridBody, GridRow, GridHead, GridCell }
